import { createRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo } from 'react';
import { AiSearchRecommendationContext } from '@/components/items/AiSearchRecommendationContext';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ItemDetailDrawer } from '@/components/items/ItemDetailDrawer';
import { ItemFilterBar } from '@/components/items/ItemFilterBar';
import { ItemMasonryGrid } from '@/components/items/ItemMasonryGrid';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { DRAWER_SEARCH_PARAM_ITEM_ID } from '@/constants/drawer-search';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useAiSearch, useItemFilters, useItems } from '@/hooks/useItems';
import { itemKeys } from '@/lib/query-keys';
import { itemListSearchSchema, normalizeItemListSearch } from '@/lib/validation';
import type { AiSearchResponse, ItemListItem } from '@/types';
import {
  buildLibraryDetailSearch,
  buildLibraryDrawerCloseSearch,
  buildLibraryDrawerOpenSearch,
  buildLibraryListSearch,
  FALLBACK_TYPE_OPTIONS,
  getFallbackTagGroups,
  getFallbackTagTerms,
  getLibraryFilterStatusMessage,
  mergeSelectedOption,
  mergeSelectedTagGroups,
  normalizeLibraryFilterSearch,
  parseMultiValueFilter,
} from './-library-route-helpers';
import { rootRoute } from './__root';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  validateSearch: (search: Record<string, unknown>) => {
    const parsed = itemListSearchSchema.safeParse(search);
    return normalizeItemListSearch(parsed.success ? parsed.data : itemListSearchSchema.parse({}));
  },
  component: LibraryHomePage,
});

const AI_SEARCH_UNAVAILABLE_MESSAGE = 'AI search is temporarily unavailable. We’ve been notified.';

function getDisplayErrorMessage(error: Error, isAiSearchError: boolean): string {
  if (!isAiSearchError) {
    return error.message;
  }

  if (/temporarily unavailable/i.test(error.message) || /notified/i.test(error.message)) {
    return AI_SEARCH_UNAVAILABLE_MESSAGE;
  }

  return error.message || AI_SEARCH_UNAVAILABLE_MESSAGE;
}

interface LibraryLoadingStateProps {
  containerAiId: string;
  contentAiId: string;
  className?: string;
}

function LibraryLoadingState({ containerAiId, contentAiId, className = '' }: LibraryLoadingStateProps) {
  return (
    <div
      className={['border border-slate-300 bg-white p-5', className].join(' ')}
      data-ai-id={containerAiId}
    >
      <div className="space-y-3" data-ai-id={contentAiId}>
        <div className="flex items-center justify-between gap-3" data-ai-id={`${contentAiId}-header`}>
          <Skeleton className="h-4 w-28" data-ai-id={`${contentAiId}-title-skeleton`} />
          <Skeleton className="h-4 w-16" data-ai-id={`${contentAiId}-meta-skeleton`} />
        </div>
        <Skeleton className="h-3 w-full" data-ai-id={`${contentAiId}-line-skeleton`} />
        <Skeleton className="h-3 w-5/6" data-ai-id={`${contentAiId}-line-skeleton`} />
      </div>
    </div>
  );
}

interface LibraryLoadMoreErrorStateProps {
  message: string;
  loading: boolean;
  onRetry: () => void;
}

function LibraryLoadMoreErrorState({ message, loading, onRetry }: LibraryLoadMoreErrorStateProps) {
  return (
    <div
      className="mt-5 border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
      data-ai-id="library-home-page-load-more-error-state"
    >
      <div
        className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
        data-ai-id="library-home-page-load-more-error-content"
      >
        <p data-ai-id="library-home-page-load-more-error-message">{message}</p>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          loading={loading}
          onClick={onRetry}
          data-ai-id="library-home-page-load-more-retry-button"
          aiIdPrefix="library-home-page-load-more-retry-button"
        >
          Try again
        </Button>
      </div>
    </div>
  );
}

function LibraryEndOfListState() {
  return (
    <div
      className="mt-5 border border-slate-300 bg-slate-50 p-4 text-center text-sm text-slate-600"
      data-ai-id="library-home-page-end-of-list-state"
    >
      You&apos;ve reached the end of the library.
    </div>
  );
}

function applyAiSearchManualSafetyFilter(
  items: ItemListItem[],
  typeValue?: string,
  selectedTagValues: string[] = [],
): ItemListItem[] {
  // The `/ai-search` endpoint applies manual type and tag filters before ranking/limiting.
  // Keep this tiny AND-only guard so stale cached data or malformed responses cannot
  // display items that contradict the active manual filters.
  return items.filter((item) => {
    if (typeValue && item.type !== typeValue) {
      return false;
    }

    if (selectedTagValues.length === 0) {
      return true;
    }

    const itemTagSlugs = new Set(item.tags.map((tag) => tag.slug));
    return selectedTagValues.every((tagSlug) => itemTagSlugs.has(tagSlug));
  });
}

function getAiSearchStatusMessage(
  query: string | undefined,
  aiSearchResponse: AiSearchResponse | undefined,
  visibleCount: number,
  serverCount: number,
  isLoading: boolean,
): string | null {
  if (!query) {
    return null;
  }

  if (isLoading) {
    return `Searching for “${query}”…`;
  }

  if (!aiSearchResponse) {
    return `AI search is ready for “${query}”.`;
  }

  const resultLabel = `${visibleCount} recommendation${visibleCount === 1 ? '' : 's'}`;
  const parts = [`Hybrid AI search returned ${resultLabel} for “${query}”.`];

  if (visibleCount !== serverCount) {
    const hiddenCount = serverCount - visibleCount;
    parts.push(`A defensive filter hid ${hiddenCount} inconsistent ${hiddenCount === 1 ? 'result' : 'results'}.`);
  }

  return parts.join(' ');
}

function getEmptyStateMessage(
  query: string | undefined,
  rawAiCount: number,
  hasManualFilters: boolean,
): string {
  if (!query) {
    return 'No items match your filters.';
  }

  if (rawAiCount === 0) {
    return `No AI search results matched “${query}”. Try a different prompt or broader wording.`;
  }

  if (hasManualFilters) {
    return 'No AI search results remain after applying your manual type and tag filters.';
  }

  return `No AI search results matched “${query}”.`;
}

function LibraryHomePage() {
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const isDesktop = useIsDesktop();
  const listFilters = useMemo(() => normalizeLibraryFilterSearch(search), [search]);
  const hasAiQuery = Boolean(listFilters.query);
  const { data: filterGroups, isLoading: isFiltersLoading, isError: isFiltersError } = useItemFilters();
  const {
    items,
    isLoading: isBrowseLoading,
    initialError,
    loadMoreError,
    loadNextPage,
    retryLoadMore,
    hasNextPage,
    isFetchingNextPage,
  } = useItems(listFilters, { enabled: !hasAiQuery });
  const aiSearchQuery = useAiSearch(listFilters, { enabled: hasAiQuery });
  const drawerItemId = search[DRAWER_SEARCH_PARAM_ITEM_ID] ?? null;
  const selectedTagValues = useMemo(() => parseMultiValueFilter(listFilters.tags), [listFilters.tags]);
  const hasManualFilters = Boolean(listFilters.type || selectedTagValues.length > 0);
  const typeOptions = mergeSelectedOption(filterGroups?.types ?? FALLBACK_TYPE_OPTIONS, listFilters.type);
  const fallbackTagGroups = useMemo(() => getFallbackTagGroups(), []);
  const fallbackTagTerms = useMemo(() => getFallbackTagTerms(), []);
  const tagGroups = useMemo(
    () => mergeSelectedTagGroups(filterGroups?.tagGroups ?? fallbackTagGroups, filterGroups?.tags ?? fallbackTagTerms, selectedTagValues),
    [fallbackTagGroups, fallbackTagTerms, filterGroups?.tagGroups, filterGroups?.tags, selectedTagValues],
  );
  const listQueryKey = useMemo(
    () => itemKeys.list(listFilters),
    [listFilters.query, listFilters.tags, listFilters.type, listFilters.ui],
  );
  const listResetKey = useMemo(() => JSON.stringify(listQueryKey), [listQueryKey]);
  const filtersStatusMessage = getLibraryFilterStatusMessage(filterGroups, isFiltersLoading, isFiltersError);
  const aiSearchItems = useMemo(
    () => applyAiSearchManualSafetyFilter(aiSearchQuery.data?.items ?? [], listFilters.type, selectedTagValues),
    [aiSearchQuery.data?.items, listFilters.type, selectedTagValues],
  );
  const rawAiResultCount = aiSearchQuery.data?.items.length ?? 0;
  const visibleItems = hasAiQuery ? aiSearchItems : items;
  const isLoading = hasAiQuery ? aiSearchQuery.isLoading : isBrowseLoading;
  const activeError = hasAiQuery
    ? (aiSearchQuery.error instanceof Error ? aiSearchQuery.error : null)
    : initialError;
  const activeErrorMessage = activeError ? getDisplayErrorMessage(activeError, hasAiQuery) : '';
  const aiSearchStatusMessage = useMemo(
    () => (
      activeError && hasAiQuery
        ? activeErrorMessage
        : getAiSearchStatusMessage(listFilters.query, aiSearchQuery.data, aiSearchItems.length, rawAiResultCount, aiSearchQuery.isLoading)
    ),
    [activeError, activeErrorMessage, aiSearchItems.length, aiSearchQuery.data, aiSearchQuery.isLoading, hasAiQuery, listFilters.query, rawAiResultCount],
  );
  const canLoadMore = Boolean(hasNextPage && !loadMoreError);
  const showEndOfListMessage = Boolean(!hasAiQuery && items.length > 0 && !hasNextPage && !loadMoreError);
  const aiSearchPlan = aiSearchQuery.data?.plan ?? null;
  const handleLoadMore = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage || loadMoreError) {
      return;
    }

    void loadNextPage();
  }, [hasNextPage, isFetchingNextPage, loadMoreError, loadNextPage]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [listResetKey]);

  useEffect(() => {
    if (isDesktop || drawerItemId === null) {
      return;
    }

    void navigate({
      to: '/items/$itemId',
      params: { itemId: String(drawerItemId) },
      search: buildLibraryDetailSearch(listFilters),
      replace: true,
    });
  }, [drawerItemId, isDesktop, listFilters, navigate]);

  return (
    <div className="space-y-6" data-ai-id="library-home-page">
      <div
        className="flex flex-col gap-5 lg:flex-row lg:items-start"
        data-ai-id="library-home-page-content"
      >
        <aside
          className="w-full shrink-0 lg:sticky lg:w-80 lg:self-start"
          data-ai-id="library-home-page-filter-sidebar"
        >
          <ItemFilterBar
            queryValue={listFilters.query}
            typeValue={listFilters.type}
            tagsValue={listFilters.tags}
            typeOptions={typeOptions}
            tagGroups={tagGroups}
            searchLoading={hasAiQuery && aiSearchQuery.isLoading}
            searchStatusMessage={aiSearchStatusMessage}
            searchStatusTone={activeError && hasAiQuery ? 'error' : 'neutral'}
            onTypeChange={(value) => {
              void navigate({
                search: () =>
                  buildLibraryListSearch({
                    query: listFilters.query,
                    type: value,
                    tags: listFilters.tags,
                  }),
                replace: true,
                resetScroll: true,
              });
            }}
            onTagsChange={(value) => {
              void navigate({
                search: () =>
                  buildLibraryListSearch({
                    query: listFilters.query,
                    type: listFilters.type,
                    tags: value,
                  }),
                replace: true,
                resetScroll: true,
              });
            }}
            onQuerySubmit={(value) => {
              void navigate({
                search: () =>
                  buildLibraryListSearch({
                    query: value,
                    type: listFilters.type,
                    tags: listFilters.tags,
                  }),
                replace: true,
                resetScroll: true,
              });
            }}
            statusMessage={filtersStatusMessage}
          />
        </aside>

        <div key={listResetKey} className="min-w-0 flex-1" data-ai-id="library-home-page-main">
          {isLoading ? (
            <LibraryLoadingState
              containerAiId="library-home-page-loading-state"
              contentAiId="library-home-page-loading-content"
            />
          ) : activeError ? (
            hasAiQuery ? (
              <div
                className="space-y-2 border border-rose-300 bg-rose-50 p-6 text-sm text-rose-700"
                role="alert"
                data-ai-id="library-home-page-ai-search-error-state"
              >
                <p className="font-semibold text-rose-800" data-ai-id="library-home-page-ai-search-error-title">
                  AI search is temporarily unavailable
                </p>
                <p data-ai-id="library-home-page-ai-search-error-message">
                  {activeErrorMessage}
                </p>
                <p className="text-xs text-rose-700/80" data-ai-id="library-home-page-ai-search-error-helper">
                  This is not an empty-results state. The live AI search runtime failed before recommendations could be loaded.
                </p>
              </div>
            ) : (
              <div className="border border-rose-300 bg-rose-50 p-6 text-sm text-rose-700" data-ai-id="library-home-page-error-state">
                {activeErrorMessage}
              </div>
            )
          ) : visibleItems.length === 0 ? (
            <div className="border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500" data-ai-id="library-home-page-empty-state">
              {getEmptyStateMessage(listFilters.query, rawAiResultCount, hasManualFilters)}
            </div>
          ) : (
            <div className="w-full" data-ai-id="library-home-page-results">
              {hasAiQuery && aiSearchPlan && listFilters.query ? (
                <AiSearchRecommendationContext
                  query={listFilters.query}
                  plan={aiSearchPlan}
                  variant="summary"
                  className="mb-5"
                  aiIdPrefix="library-home-page-ai-recommendation-context"
                />
              ) : null}

              {hasAiQuery ? (
                <ItemMasonryGrid
                  items={visibleItems}
                  aiSearchPlan={aiSearchPlan}
                  aiSearchQuery={listFilters.query}
                  onItemClick={(item) => {
                    if (isDesktop) {
                      void navigate({
                        search: buildLibraryDrawerOpenSearch(listFilters, item.id),
                        replace: true,
                        resetScroll: false,
                      });
                      return;
                    }

                    void navigate({
                      to: '/items/$itemId',
                      params: { itemId: String(item.id) },
                      search: buildLibraryDetailSearch(listFilters),
                    });
                  }}
                />
              ) : (
                <InfiniteScroll
                  dataLength={visibleItems.length}
                  next={handleLoadMore}
                  hasMore={canLoadMore}
                  loader={
                    <LibraryLoadingState
                      containerAiId="library-home-page-loading-more-state"
                      contentAiId="library-home-page-loading-more-content"
                      className="mt-5"
                    />
                  }
                  endMessage={showEndOfListMessage ? <LibraryEndOfListState /> : null}
                  scrollThreshold="240px"
                  style={{ overflow: 'visible' }}
                >
                  <ItemMasonryGrid
                    items={visibleItems}
                    onItemClick={(item) => {
                      if (isDesktop) {
                        void navigate({
                          search: buildLibraryDrawerOpenSearch(listFilters, item.id),
                          replace: true,
                          resetScroll: false,
                        });
                        return;
                      }

                      void navigate({
                        to: '/items/$itemId',
                        params: { itemId: String(item.id) },
                        search: buildLibraryDetailSearch(listFilters),
                      });
                    }}
                  />

                  {loadMoreError ? (
                    <LibraryLoadMoreErrorState
                      message={loadMoreError.message}
                      loading={isFetchingNextPage}
                      onRetry={() => {
                        void retryLoadMore();
                      }}
                    />
                  ) : null}
                </InfiniteScroll>
              )}
            </div>
          )}
        </div>
      </div>

      <div data-ai-id="library-home-page-drawer">
        <ItemDetailDrawer
          open={isDesktop && drawerItemId !== null}
          itemId={drawerItemId}
          backToSearch={buildLibraryDetailSearch(listFilters)}
          onClose={() => {
            void navigate({
              search: buildLibraryDrawerCloseSearch(listFilters),
              replace: true,
              resetScroll: false,
            });
          }}
        />
      </div>
    </div>
  );
}
